/**
 * The modal scaffold every dialog in the app sits in: a navy scrim that
 * closes on click, Escape to close, one white card. Lifted from the
 * Clients page (task 69) so the Integrations pages and, later, the
 * Response panel (task 83) share one shell rather than three copies.
 */

import { useEffect, type ReactNode } from 'react';

import { ApiError, describeError } from '@bluesphere/api-client';

export function ModalShell({
  onClose,
  children,
  width = 'max-w-md',
}: {
  onClose: () => void;
  children: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 bg-bluesphere-primary/60"
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className={`pointer-events-auto w-full rounded-cardInner bg-white shadow-2xl ${width}`}
        >
          {children}
        </div>
      </div>
    </>
  );
}

/** The one text-input class, so every form field in a modal matches. */
export const INPUT =
  'mt-1 block w-full rounded-input border border-bluesphere-borderMuted px-3 py-2 text-sm text-bluesphere-textOnLight placeholder-bluesphere-muted focus:border-bluesphere-secondary focus:outline-hidden focus:ring-1 focus:ring-bluesphere-secondary';

/** An API error inside a form, with the correlation id the API attaches. */
export function ErrorNote({ error }: { error: unknown }) {
  if (error == null) return null;
  return (
    <div
      className="rounded-input border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
      role="alert"
    >
      {describeError(error)}
    </div>
  );
}

/**
 * A connect's error, with Story 45's prompt for a 409 `service_required`
 * (task 179c): the source belongs to a service this Client is not engaged
 * for. A platform Admin may add it with the connect, in one audited step;
 * anyone else is told who can. Declining is closing the form: nothing is
 * half-connected. Any other error reads as `ErrorNote` does.
 */
export function ConnectErrorNote({
  error,
  canAddService,
  onAddService,
  isPending = false,
}: {
  error: unknown;
  canAddService: boolean;
  onAddService: () => void;
  isPending?: boolean;
}) {
  if (!(error instanceof ApiError) || error.code !== 'service_required') {
    return <ErrorNote error={error} />;
  }
  const service = String(
    error.body.details?.display_name ?? error.body.details?.service ?? 'another service',
  );
  return (
    <div
      className="space-y-2 rounded-input border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      role="alert"
    >
      <p>
        {service} covers this source, and this Client is not engaged for {service}.
        {canAddService
          ? ` Add ${service} to this Client and connect?`
          : ' A Simulint platform Admin can add it.'}
      </p>
      {canAddService && (
        <button
          type="button"
          onClick={onAddService}
          disabled={isPending}
          className="rounded-full bg-amber-900 px-3 py-1 font-medium text-white disabled:opacity-50"
        >
          {isPending ? 'Adding…' : `Add ${service} and connect`}
        </button>
      )}
    </div>
  );
}
